import { events } from '../../../analytics/events';
import { ExpandComponent } from './ExpandComponent';
import { LegalNotice } from './LegalNotice';
import { Paper } from '../../../models/result-set';
import { PaperSubContentComponent } from './PaperSubContentComponent';
import { Summary } from './Summary';
import { useTrackedHandler } from '../../../hooks/useTracking';

import { useState } from 'react';
import styled from 'styled-components';

interface PaperContentComponentProps {
  paper: Paper;
}

export function PaperContentComponent(
  props: PaperContentComponentProps,
): JSX.Element {
  const { paper } = props;
  const [isExpanded, setIsExpanded] = useState(false);
  const hasSummary = !!paper.relevanceJudgement?.relevanceSummary;

  const handleEvidenceExpandedChange = useTrackedHandler(
    events.panels.ShowEvidenceViewChanged,
    (isExpanded: boolean) => {
      setIsExpanded(isExpanded);
    },
    (isExpanded) => ({ isExpanded, corpusId: paper.corpusId }),
  );

  const reportAbstractExpandedChange = useTrackedHandler(
    events.panels.ShowEvidenceAbstractChanged,
    (_: boolean) => {},
    (isExpanded) => ({ isExpanded, corpusId: paper.corpusId }),
  );

  const reportSnippetsExpandedChange = useTrackedHandler(
    events.panels.ShowEvidenceSnippetsChanged,
    (_: boolean) => {},
    (isExpanded) => ({ isExpanded, corpusId: paper.corpusId }),
  );

  const reportCitationsExpandedChange = useTrackedHandler(
    events.panels.ShowEvidenceCitationsChanged,
    (_: boolean) => {},
    (isExpanded) => ({ isExpanded, corpusId: paper.corpusId }),
  );

  return (
    <MainLayout className={hasSummary ? 'has-summary' : undefined}>
      {hasSummary && (
        <Summary
          relevanceJudgement={paper.relevanceJudgement?.relevance}
          relevanceScore={paper.relevanceScore}
          text={paper.relevanceJudgement?.relevanceSummary}
          relevanceCriteriaJudgements={
            paper.relevanceJudgement?.relevanceCriteriaJudgements ?? []
          }
          isExpanded={isExpanded}
          setIsExpanded={handleEvidenceExpandedChange}
          isLegalToShow={paper.legalToShow}
        />
      )}
      {!paper.legalToShow && isExpanded && (
        <LegalNotice
          corpusId={paper.corpusId}
          paperUrl={paper.url}
        ></LegalNotice>
      )}
      {(isExpanded || !hasSummary) && (
        <SubContentList>
          <PaperSubContentComponent
            key="abstract"
            subContentType="abstract"
            paper={paper}
            onExpandedChange={reportAbstractExpandedChange}
          />
          <PaperSubContentComponent
            key="snippets"
            subContentType="snippets"
            paper={paper}
            onExpandedChange={reportSnippetsExpandedChange}
          />
          <PaperSubContentComponent
            key="citations"
            subContentType="citations"
            paper={paper}
            onExpandedChange={reportCitationsExpandedChange}
          />
          {hasSummary && (
            <ContentFooterDiv>
              <ExpandComponent
                isExpanded={isExpanded}
                setExpanded={handleEvidenceExpandedChange}
              />
            </ContentFooterDiv>
          )}
        </SubContentList>
      )}
    </MainLayout>
  );
}

const ContentFooterDiv = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  width: 100%;
`;

const MainLayout = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  border-radius: ${({ theme }) => theme.border.radius.mediumLarge};

  background-color: transparent;
  padding: 0px;

  &.has-summary {
    background-color: ${({ theme }) => theme.color.background.paper.primary};
    padding: 12px;
  }

  margin: 10px 0px;
  gap: 8px;
`;

const SubContentList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  width: 100%;
`;
